/**
 * ModelCard - single row inside the model dropdown list.
 *
 * Handles both local (installed) models and CivitAI catalog entries.
 * Local models are selectable; CivitAI models are display-only with a
 * "Desktop Only" overlay in serverless mode.
 */

import { memo, useCallback } from 'react';
import { Star, Download, Heart, ImageOff, CheckCircle2, Monitor } from 'lucide-react';
import { useTranslation } from '@/hooks/useTranslation';
import { isServerlessMode } from '@/utils/apiMode';
import type { ImageModelInfo } from '@/types';
import { ORIENTATION_LABELS, deriveOrientation, formatCount } from './modelSelectorUtils';

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

export interface ModelCardProps {
  model: ImageModelInfo;
  isSelected: boolean;
  brokenImages: Set<string>;
  onSelect: (model: ImageModelInfo) => void;
  onImageBroken: (key: string) => void;
  /** 'local' renders an interactive button; 'civitai' renders a read-only div */
  variant: 'local' | 'civitai';
}

// ---------------------------------------------------------------------------
// Sub-renders
// ---------------------------------------------------------------------------

function ModelThumbnail({
  model,
  size,
  brokenImages,
  onImageBroken,
}: {
  model: ImageModelInfo;
  size: 'sm' | 'md';
  brokenImages: Set<string>;
  onImageBroken: (key: string) => void;
}) {
  const sizeClass = size === 'md' ? 'w-12 h-12' : 'w-10 h-10';
  const imageKey = model.image_url || model.id;

  if (model.image_url && !brokenImages.has(imageKey)) {
    return (
      <img
        src={model.image_url}
        alt=""
        className={`${sizeClass} rounded object-cover flex-shrink-0 bg-black/30`}
        loading="lazy"
        onError={() => onImageBroken(imageKey)}
      />
    );
  }

  return (
    <div
      className={`${sizeClass} rounded bg-white/5 flex items-center justify-center flex-shrink-0`}
    >
      <ImageOff className="h-4 w-4 text-white/20" />
    </div>
  );
}

function TrainingBadges({
  trainingResolution,
  base,
}: {
  trainingResolution: string | null | undefined;
  base: string;
}) {
  if (!trainingResolution) {
    return (
      <span className="text-[10px] bg-white/5 text-white/50 px-1.5 py-0.5 rounded flex-shrink-0 italic">
        Multi-aspect {base}
      </span>
    );
  }
  const orientation = deriveOrientation(trainingResolution);
  return (
    <>
      {orientation && (
        <span className="text-[10px] bg-blue-900/40 text-blue-300 px-1.5 py-0.5 rounded flex-shrink-0">
          {ORIENTATION_LABELS[orientation]}
        </span>
      )}
      <span className="text-[10px] bg-purple-900/40 text-purple-200 px-1.5 py-0.5 rounded flex-shrink-0 font-mono">
        {trainingResolution}
      </span>
    </>
  );
}

function StatsRow({ model }: { model: ImageModelInfo }) {
  const { t } = useTranslation();
  if (!model.stats && !model.creator) return null;
  return (
    <div className="flex items-center gap-3 mt-0.5">
      {model.creator && (
        <span className="text-[11px] text-white/40">
          {t('image.model.by', { defaultValue: 'by' })} {model.creator}
        </span>
      )}
      {model.stats && (
        <>
          <span className="inline-flex items-center gap-1 text-[11px] text-white/40">
            <Download className="h-3 w-3" />
            {formatCount(model.stats.downloadCount)}
          </span>
          <span className="inline-flex items-center gap-1 text-[11px] text-white/40">
            <Heart className="h-3 w-3" />
            {formatCount(model.stats.favoriteCount)}
          </span>
        </>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export const ModelCard = memo(function ModelCard({
  model,
  isSelected,
  brokenImages,
  onSelect,
  onImageBroken,
  variant,
}: ModelCardProps) {
  const { t } = useTranslation();

  const handleClick = useCallback(() => {
    onSelect(model);
  }, [onSelect, model]);

  const handleDesktopOverlayClick = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    window.dispatchEvent(new CustomEvent('navigate-to-mode', { detail: 'download' }));
  }, []);

  if (variant === 'local') {
    return (
      <button
        type="button"
        onClick={handleClick}
        className={`w-full flex items-center gap-3 px-3 py-2.5 min-h-[44px] text-left transition-colors cursor-pointer ${
          isSelected
            ? 'bg-primary-900/30 border-l-2 border-primary-500'
            : 'hover:bg-white/5 border-l-2 border-transparent'
        }`}
      >
        <ModelThumbnail
          model={model}
          size="md"
          brokenImages={brokenImages}
          onImageBroken={onImageBroken}
        />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm font-medium text-white truncate">{model.name}</span>
            {model.recommended && (
              <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] font-bold bg-amber-900/50 text-amber-300 flex-shrink-0">
                <Star className="h-2.5 w-2.5" />
                {t('common.recommended', { defaultValue: 'Recommended' })}
              </span>
            )}
            {model.is_installed === false ? (
              <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] bg-sky-900/40 text-sky-300 flex-shrink-0">
                <Download className="h-2.5 w-2.5" />
                {model.size_mb
                  ? t('image.model.downloadOnFirstUseSized', {
                      defaultValue: 'Downloads on first use ({{size}} MB)',
                      size: model.size_mb,
                    })
                  : t('image.model.downloadOnFirstUse', { defaultValue: 'Downloads on first use' })}
              </span>
            ) : (
              <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] bg-green-900/40 text-green-300 flex-shrink-0">
                <CheckCircle2 className="h-2.5 w-2.5" />
                {t('image.model.installed', { defaultValue: 'Installed' })}
              </span>
            )}
            <span className="text-[10px] px-1.5 py-0.5 rounded bg-white/10 text-white/50 flex-shrink-0">
              {model.base}
            </span>
            <TrainingBadges trainingResolution={model.training_resolution} base={model.base} />
            {model.nsfw && (
              <span className="text-[10px] bg-red-600/20 text-red-400 border border-red-500/30 px-1.5 py-0.5 rounded flex-shrink-0">
                {t('image.model.nsfwBadge', { defaultValue: 'NSFW' })}
              </span>
            )}
          </div>
          {model.description && (
            <p className="text-xs text-white/50 line-clamp-2 mt-0.5">{model.description}</p>
          )}
          <StatsRow model={model} />
        </div>
      </button>
    );
  }

  // CivitAI variant - display only, not selectable
  return (
    <div className="relative w-full flex items-center gap-3 px-3 py-2.5 min-h-[44px] text-left opacity-60 cursor-not-allowed border-l-2 border-transparent">
      {isServerlessMode() && (
        <div
          className="absolute inset-0 z-10 flex items-center justify-center pointer-events-auto cursor-pointer hover:bg-black/20 transition-colors group/badge"
          onClick={handleDesktopOverlayClick}
        >
          <div className="bg-emerald-900/80 backdrop-blur-sm rounded-lg px-3 py-2 flex items-center gap-1.5 border border-emerald-500/30 shadow-lg shadow-emerald-500/10 group-hover/badge:border-emerald-400/50 group-hover/badge:shadow-emerald-500/20 transition-all">
            <Monitor className="w-3.5 h-3.5 text-emerald-400" />
            <span className="text-[10px] text-emerald-300 font-semibold">
              {t('image.model.downloadDesktopApp', { defaultValue: 'Download Desktop App' })}
            </span>
          </div>
        </div>
      )}
      <ModelThumbnail
        model={model}
        size="sm"
        brokenImages={brokenImages}
        onImageBroken={onImageBroken}
      />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-sm font-medium text-white truncate">{model.name}</span>
          <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] bg-emerald-900/40 text-emerald-300 flex-shrink-0">
            <Monitor className="h-2.5 w-2.5" />
            {t('image.model.desktopOnly', { defaultValue: 'Desktop Only' })}
          </span>
        </div>
        {model.description && (
          <p className="text-xs text-white/40 line-clamp-1 mt-0.5">{model.description}</p>
        )}
        <StatsRow model={model} />
      </div>
    </div>
  );
});

export default ModelCard;
